import { BorderGridContainer, ContentTiles } from '@ahmdigital/ui';
import { isEmpty, kebabCase, map } from 'lodash/fp';
import portableTextToPlain from '@ahmdigital/logic/lib/content/utils/portable-text-to-plain';

import { AcquisitionLandingContent } from '../../types';
import { CONTENT_TYPE } from '../../../../cms/constants';
import CommonQuestionsSection from '../../../../components/sections/common-questions-section';
import ContentTilesSection from '../../../../components/content-tiles-section';
import ExternalContent from '../../../insurance/external-content';
import FindCoverSection from '../../../media-landing/components/find-cover-section';
import getContentTilesData from '../../../explained-term/detail/utils/get-content-tiles-data';
import getFormattedQuestionForAccordion from '../../../../utils/get-formatted-question-for-accordion';
import getImageAttributes from '../../../../cms/utils/get-image-attributes';
import mapValuePropositionsContent from '../../../insurance/utils/combine-content-with-cms-data/map-value-propositions-content';
import questionsHero from '../../../../images/questions-hero.svg';
import ValuePropositions from '../../../insurance/components/value-propositions';

const getContentToRender = (content: AcquisitionLandingContent[]) =>
  map((section) => {
    const contentType = section?.__typename;

    switch (contentType) {
      case CONTENT_TYPE.SALES_JOURNEY: {
        const { imageObject, descriptionRaw, name, urlObject } = section;
        const subtitle = portableTextToPlain(descriptionRaw);

        const ctas = urlObject
          ? [{ link: urlObject.url ?? '', linkText: urlObject.name ?? undefined, variant: ['flair'] }]
          : undefined;
        const image = getImageAttributes({ height: 240, imageObject });
        const formattedTile = {
          ctas,
          id: urlObject?.url ?? name ?? '',
          image: isEmpty(image) ? undefined : image,
          subtitle,
          title: name ?? '',
          variant: 'vertical',
        };

        return (
          <BorderGridContainer hideBorderTop>
            <ContentTiles tiles={[formattedTile]} />
          </BorderGridContainer>
        );
      }
      case CONTENT_TYPE.PRODUCT_LIST_SECTION: {
        const { title, productLists } = section;
        return productLists ? <FindCoverSection name={title} productLists={productLists} /> : null;
      }
      case CONTENT_TYPE.QUESTIONS_LIST: {
        const { questions, title } = section;
        const image = {
          alt: 'Billboard stating "Got questions? We’ve got the answers"',
          src: questionsHero.src,
        };
        const filteredQuestions = questions?.filter(
          (question): question is NonNullable<typeof question> => question != null,
        );

        const formattedQuestions = getFormattedQuestionForAccordion(filteredQuestions);
        return (
          <CommonQuestionsSection
            accordionId={`acquisition-landing-common-questions-${kebabCase(title ?? '')}`}
            image={image}
            questions={formattedQuestions}
            title={title ?? undefined}
            withBorder
          />
        );
      }
      case CONTENT_TYPE.PROMOTIONAL_CTA_LIST: {
        const { items } = section;
        const filteredItems = items?.filter((item): item is NonNullable<typeof item> => item != null);

        if (isEmpty(filteredItems)) {
          return null;
        }

        const contentTiles = getContentTilesData({ serviceTiles: filteredItems });
        return <ContentTilesSection contentTiles={contentTiles} />;
      }
      case CONTENT_TYPE.VALUE_PROPOSITIONS_GROUP: {
        const cmsSectionContent = section;
        return <ValuePropositions {...mapValuePropositionsContent({ cmsSectionContent })} />;
      }
      case CONTENT_TYPE.EXTERNAL_CONTENT: {
        const identifier = section.identifier?.current;
        const { title } = section;
        if (!identifier) return null;
        return <ExternalContent identifier={identifier} title={title} />;
      }
      default:
        return null;
    }
  }, content);

export default getContentToRender;
